import type { ITenantDailyDistributionItem } from '@experiences/interfaces';
import { useApolloTheme } from '@experiences/theme';
import {
    Box,
    useTheme,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import createStyles from '@mui/styles/createStyles';
import {
    Border,
    Colors,
    FontVariantToken,
    Shadow,
    Spacing,
} from '@uipath/apollo-core';
import { Chart } from '@uipath/apollo-lab/react';
import { ApTypography } from '@uipath/portal-shell-react';
import React, {
    useCallback,
    useMemo,
    useState,
} from 'react';
import { useIntl } from 'react-intl';

import { customLegendRenderer } from '../../../common/ApolloLab/CustomLegendRenderer';
import { useConsumptionTimezone } from '../hooks/useConsumptionTimezone';
import { formatConsumptionValue } from '../utils';

type PeriodType = 'month' | 'week';

interface DistributionOfConsumptionProps {
    unitType: string;
    data: ITenantDailyDistributionItem[];
}

const useStyles = makeStyles(theme =>
    createStyles({
        container: {
            width: '100%',
            minHeight: 0,
            height: '100%',
            overflow: 'hidden',
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
        },
        switchRow: {
            display: 'flex',
            justifyContent: 'flex-start',
            marginBottom: Spacing.SpacingS,
        },
        periodSwitch: {
            display: 'flex',
            alignItems: 'center',
            gap: Spacing.SpacingMicro,
            padding: Spacing.SpacingMicro,
            borderRadius: Border.BorderRadiusL,
            backgroundColor: theme.palette.semantic.colorBackgroundSecondary,
        },
        periodSwitchOption: {
            padding: `${Spacing.SpacingXs} ${Spacing.SpacingBase}`,
            borderRadius: Border.BorderRadiusL,
            cursor: 'pointer',
            backgroundColor: 'transparent',
            color: theme.palette.semantic.colorForegroundDeEmp,
        },
        periodSwitchOptionSelected: {
            backgroundColor: theme.palette.semantic.colorBackground,
            color: theme.palette.semantic.colorForeground,
            boxShadow: Shadow.ShadowDp1,
        },
        chartContainer: {
            minHeight: 0,
            flex: 1,
            overflow: 'hidden',
        },
    })
);

const getWeekStartUtc = (date: Date): Date => {
    const weekStart = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
    const day = weekStart.getUTCDay();
    const diff = day === 0 ? -6 : 1 - day;
    weekStart.setUTCDate(weekStart.getUTCDate() + diff);
    return weekStart;
};

const FUTURE_PERIODS_INCLUDED = 1;

const getFirstExcludedPeriodStart = (periodType: PeriodType, now: Date = new Date()): Date => {
    const periodsToSkip = FUTURE_PERIODS_INCLUDED + 1;

    if (periodType === 'month') {
        return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + periodsToSkip, 1));
    }
    const todayUtc = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
    const cutoff = new Date(getWeekStartUtc(todayUtc));
    cutoff.setUTCDate(cutoff.getUTCDate() + periodsToSkip * 7);
    return cutoff;
};

export const DistributionOfConsumption: React.FC<DistributionOfConsumptionProps> = ({
    unitType,
    data: distributionData,
}) => {
    const classes = useStyles();
    const { formatMessage: translate } = useIntl();
    const { themeId } = useApolloTheme();
    const theme = useTheme();
    const [ periodType, setPeriodType ] = useState<PeriodType>('month');
    const { timezoneId } = useConsumptionTimezone();

    // buckets are calendar days in the display zone, so "today" must anchor in that zone
    const zoneTodayUtc = useMemo(() => {
        const dayKey = new Intl.DateTimeFormat('en-CA', {
            timeZone: timezoneId,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
        }).format(Date.now());
        return new Date(`${dayKey}T00:00:00Z`);
    }, [ timezoneId ]);

    const {
        data, labels, chartDescription,
    } = useMemo(() => {
        const cutoffDate = getFirstExcludedPeriodStart(periodType, zoneTodayUtc);

        const groupedMap = new Map<string, { label: string; consumedAmount: number }>();

        distributionData.forEach((item) => {
            const itemDate = new Date(`${item.date}T00:00:00Z`);
            if (Number.isNaN(itemDate.getTime())) {
                return;
            }

            if (itemDate.getTime() >= cutoffDate.getTime()) {
                return;
            }

            if (periodType === 'month') {
                const monthKey = `${itemDate.getUTCFullYear()}-${String(itemDate.getUTCMonth() + 1).padStart(2, '0')}`;
                const monthLabel = itemDate.toLocaleDateString(undefined, {
                    month: 'short',
                    year: 'numeric',
                    timeZone: 'UTC',
                });
                const current = groupedMap.get(monthKey);
                groupedMap.set(monthKey, {
                    label: monthLabel,
                    consumedAmount: (current?.consumedAmount ?? 0) + item.totalConsumedAmount,
                });
                return;
            }

            const weekStart = getWeekStartUtc(itemDate);
            const weekKey = weekStart.toISOString()
                .slice(0, 10);
            const weekLabel = `${translate({ id: 'CLIENT_CONSUMPTION_DASHBOARD_ANALYTICS_PERIOD_WEEK' })} ${weekStart.toLocaleDateString(undefined, {
                month: 'short',
                day: 'numeric',
                timeZone: 'UTC',
            })}`;
            const current = groupedMap.get(weekKey);
            groupedMap.set(weekKey, {
                label: weekLabel,
                consumedAmount: (current?.consumedAmount ?? 0) + item.totalConsumedAmount,
            });
        });

        const sortedEntries = [ ...groupedMap.entries() ].sort(([ leftKey ], [ rightKey ]) => leftKey.localeCompare(rightKey));
        const aggregatedData = sortedEntries.map(([ , value ]) => value.consumedAmount);
        const aggregatedLabels = sortedEntries.map(([ , value ]) => value.label);
        const hasAnyConsumption = aggregatedData.some(value => value > 0);

        return {
            data: hasAnyConsumption ? aggregatedData : [],
            labels: hasAnyConsumption ? aggregatedLabels : [],
            chartDescription: translate({ id: 'CLIENT_CONSUMPTION_DASHBOARD_ANALYTICS_DISTRIBUTION_CHART_ALT' }),
        };
    }, [ distributionData, periodType, translate, zoneTodayUtc ]);

    const scaleY = useMemo(() => ({
        type: 'linear' as const,
        display: true,
        label: translate(
            { id: 'CLIENT_CONSUMPTION_DASHBOARD_ANALYTICS_CHART_Y_LABEL' },
            { unitType }
        ),
    }), [ translate, unitType ]);

    const hasConsumptionData = data.length > 0;
    const chartData = useMemo(() => [
        {
            data,
            renderer: 'bar' as const,
            isCompareData: false,
            color: themeId.includes('light') ? Colors.ColorBlueSecondary500 : Colors.ColorBlueSecondary300,
        },
    ], [ data, themeId ]);

    const tooltipSchemaBuilder = useCallback((values: Parameters<NonNullable<React.ComponentProps<typeof Chart>['tooltipSchemaBuilder']>>[0]) =>
        values.map((value) => {
            const formattedY = typeof value.value.y === 'number'
                ? formatConsumptionValue(value.value.y)
                : value.value.y;
            const text = `${value.value.x}: ${formattedY}`;
            return {
                cells: [
                    {
                        text,
                        color: value.color,
                        content: customLegendRenderer({
                            borderColor: theme.palette.semantic.colorBackground,
                            color: value.color,
                            text,
                        }),
                    },
                ],
            };
        }), [ theme.palette.semantic.colorBackground ]);

    const legendProperties = useMemo(() => ({ hasLegend: false }), []);
    return (
        <Box className={classes.container}>
            {hasConsumptionData ? (
                <Box className={classes.switchRow}>
                    <Box className={classes.periodSwitch}>
                        <Box
                            role="button"
                            tabIndex={0}
                            aria-pressed={periodType === 'month'}
                            className={`${classes.periodSwitchOption} ${periodType === 'month' ? classes.periodSwitchOptionSelected : ''}`}
                            onClick={() => setPeriodType('month')}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    setPeriodType('month');
                                }
                            }}
                        >
                            <ApTypography variant={FontVariantToken.fontSizeS}>
                                {translate({ id: 'CLIENT_CONSUMPTION_DASHBOARD_ANALYTICS_PERIOD_MONTH' })}
                            </ApTypography>
                        </Box>
                        <Box
                            role="button"
                            tabIndex={0}
                            aria-pressed={periodType === 'week'}
                            className={`${classes.periodSwitchOption} ${periodType === 'week' ? classes.periodSwitchOptionSelected : ''}`}
                            onClick={() => setPeriodType('week')}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    setPeriodType('week');
                                }
                            }}
                        >
                            <ApTypography variant={FontVariantToken.fontSizeS}>
                                {translate({ id: 'CLIENT_CONSUMPTION_DASHBOARD_ANALYTICS_PERIOD_WEEK' })}
                            </ApTypography>
                        </Box>
                    </Box>
                </Box>
            ) : null}
            <Box className={classes.chartContainer}>
                {hasConsumptionData ? (
                    <Chart
                        data={chartData}
                        labels={labels}
                        scaleY={scaleY}
                        tooltipSchemaBuilder={tooltipSchemaBuilder}
                        legendProperties={legendProperties}
                        maximumBarWidth={40}
                        alt={`${translate({ id: 'CLIENT_CONSUMABLE_GRAPH_SHORT_NAME' })} - ${chartDescription}`}
                        chartInstructions={`${translate({ id: 'CHART_INSTRUCTIONS_NAVIGATE_WITH_KEYBOARD' })}`}
                    />) : (
                    <Chart
                        data={undefined}
                        messageMapper={() => translate({ id: 'CLIENT_CONSUMPTION_DASHBOARD_ANALYTICS_EMPTY_DISTRIBUTION' })}
                    />
                )}
            </Box>
        </Box>
    );
};
